import { geocodeByAddress, geocodeByPlaceId } from 'react-places-autocomplete'
import { call, put, select, take } from 'redux-saga/effects'
import { push } from 'connected-react-router'
import { formatUrl } from '../../util/formatUtils'
import { pascalcaseKeys } from '../../util/keys'
import { logException } from '../../util/errorUtils'
import { searchNearbyLocations, submitBuilding } from '../../api/api'
import { apiSaga, formSaga } from './sagas'
import {
  locationRoute,
  noLocationRoute,
  selectLocationRoute
} from '../../routes/routes'
import {
  getAllDropoffs,
  selectUserLocationId,
  selectLocationError
} from './selectors'

const CANT_PARSE_ADDRESS = 'There was a problem finding this address.'
// ------------------------------------
// Action Types & Creators
// ------------------------------------

export const UPDATE = 'foodsby/locationSearch/UPDATE'
export const SEARCH = 'foodsby/locationSearch/SEARCH'
export const SUCCESS = 'foodsby/locationSearch/SUCCESS'
export const FAIL = 'foodsby/locationSearch/FAIL'
export const CLEAR_ERROR = 'foodsby/locationSearch/CLEAR_ERROR'
export const FORMATTED_ADDRESS = 'foodsby/locationSearch/FORMATTED_ADDRESS'
export const SUBMIT_BUILDING = 'foodsby/locationSearch/SUBMIT_BUILDING'
export const SUBMIT_BUILDING_SUCCESS =
  'foodsby/locationSearch/SUBMIT_BUILDING_SUCCESS'
export const SUBMIT_BUILDING_FAILURE =
  'foodsby/locationSearch/SUBMIT_BUILDING_FAILURE'
export const LOAD_LOCATIONS_FROM_ID =
  'foodsby/locationSearch/LOAD_LOCATIONS_FROM_ID'
export const LOAD_LOCATIONS_BY_ID_SUCCESS =
  'foodsby/locationSearch/LOAD_LOCATIONS_BY_ID_SUCCESS'
export const LOAD_LOCATION_BY_ID_FAILURE =
  'foodsby/locationSearch/LOAD_LOCATION_BY_ID_FAILURE'
export const FORMAT_ADDRESS_FROM_ADDRESSID =
  'foodsby/locationSearch/FORMAT_ADDRESS_FROM_ADDRESSID'

export function update(address) {
  return { type: UPDATE, payload: address }
}

export function search(address, redirect = true) {
  return { type: SEARCH, payload: { address, redirect } }
}

export function formattedSearchAddress(address) {
  return { type: FORMATTED_ADDRESS, payload: address }
}

export function locationSearchSucceeded(response) {
  return {
    type: SUCCESS,
    payload: response
  }
}

export function locationSearchFailed(response) {
  return {
    type: FAIL,
    error: response
  }
}

export function clearError() {
  return {
    type: CLEAR_ERROR
  }
}

export function submitBuildingStart(submitBuildingFormData) {
  return {
    type: SUBMIT_BUILDING,
    payload: { submitBuildingFormData }
  }
}

export function submitBuildingSuccess(response) {
  return {
    type: SUBMIT_BUILDING_SUCCESS,
    payload: { response }
  }
}

export function submitBuildingFailure(error) {
  return {
    type: SUBMIT_BUILDING_FAILURE,
    error
  }
}

export const loadLocationsFromAddressIdStart = id => {
  return {
    type: LOAD_LOCATIONS_FROM_ID,
    payload: { id }
  }
}

export const loadLocationsByIdSuccess = locations => {
  return {
    type: LOAD_LOCATIONS_BY_ID_SUCCESS,
    payload: { locations }
  }
}

export const loadLocationByIdFailure = error => {
  return {
    type: LOAD_LOCATION_BY_ID_FAILURE,
    error
  }
}

export const formatFromAddressIdStart = id => {
  return {
    type: FORMAT_ADDRESS_FROM_ADDRESSID,
    payload: { id }
  }
}

// ------------------------------------
// Action Handlers
// ------------------------------------

const ACTION_HANDLERS = {
  [UPDATE]: (state, action) => {
    return {
      ...state,
      address: action.payload,
      error: undefined
    }
  },
  [SEARCH]: (state, action) => {
    return {
      ...state,
      loading: true,
      address: action.payload.address,
      submitting: true,
      error: undefined
    }
  },
  [FORMATTED_ADDRESS]: (state, action) => {
    return {
      ...state,
      formattedSearchAddress: action.payload
    }
  },
  [SUCCESS]: (state, action) => {
    return {
      ...state,
      locations: action.payload,
      loading: false,
      submitting: undefined,
      address: undefined
    }
  },
  [FAIL]: (state, action) => {
    return {
      ...state,
      error: action.error,
      loading: false,
      submitting: undefined
    }
  },
  [CLEAR_ERROR]: (state, action) => {
    return {
      ...state,
      error: undefined
    }
  },
  [SUBMIT_BUILDING]: state => {
    return {
      ...state,
      submitting: true,
      error: undefined
    }
  },
  [SUBMIT_BUILDING_SUCCESS]: (state, action) => {
    return {
      ...state,
      success: true,
      submitting: undefined,
      formattedSearchAddress: undefined,
      address: undefined
    }
  },
  [SUBMIT_BUILDING_FAILURE]: (state, action) => {
    return {
      ...state,
      error: action.error.response ? action.error.data : action.error.message,
      success: false,
      submitting: undefined
    }
  },
  [LOAD_LOCATIONS_FROM_ID]: (state, action) => {
    const {
      payload: { id }
    } = action
    return {
      ...state,
      id,
      isLocationLoading: true
    }
  },
  [LOAD_LOCATIONS_BY_ID_SUCCESS]: (state, action) => {
    const {
      payload: { locations }
    } = action
    return {
      ...state,
      locations,
      isLocationLoading: false,
      error: undefined
    }
  },
  [LOAD_LOCATION_BY_ID_FAILURE]: (state, action) => {
    const { error } = action
    return {
      ...state,
      isLocationLoading: false,
      locations: undefined,
      error
    }
  }
}

// ------------------------------------
// Reducer
// ------------------------------------

const initialState = {
  locations: {
    items: []
  }
}

export default function locationSearch(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type]

  return handler ? handler(state, action) : state
}

// ------------------------------------
// Helpers
// ------------------------------------

export function formatAddress(address) {
  const formattedSearchAddress = {
    street: '',
    city: '',
    state: '',
    zip: ''
  }
  if (address && address.address_components) {
    address.address_components.forEach(addr => {
      addr.types.forEach(type => {
        switch (type) {
          case 'street_number':
            formattedSearchAddress.street = addr.long_name
            break
          case 'route':
            formattedSearchAddress.street += formattedSearchAddress.street
              ? ` ${addr.long_name}`
              : addr.long_name
            break
          case 'sublocality':
          case 'locality':
          case 'neighborhood':
            formattedSearchAddress.city = addr.long_name
            break
          case 'administrative_area_level_1':
            formattedSearchAddress.state = addr.long_name
            break
          case 'postal_code':
            formattedSearchAddress.zip = addr.short_name
            break
          default:
            break
        }
      })
    })
  }
  return formattedSearchAddress
}

export async function convertAddress(address) {
  const results = await geocodeByAddress(address)
  if (results.length) {
    try {
      if (results[0] && results[0].formatted_address) {
        const addressComponents = formatAddress(results[0])

        const finalResult = await geocodeByAddress(results[0].formatted_address)
        let latLng = {
          lat: finalResult[0].geometry.location.lat(),
          lng: finalResult[0].geometry.location.lng()
        }
        const formattedAddress = formatAddress(finalResult[0])
        // if we didn't get one of the components, try looking it up by ID
        if (
          !addressComponents.zip ||
          !addressComponents.street ||
          !addressComponents.city ||
          !addressComponents.state
        ) {
          const {
            address: formattedSearchAddress,
            latLng
          } = await getAddressByPlaceId(finalResult[0].place_id)
          addressComponents.zip =
            addressComponents.zip ||
            formattedAddress.zip ||
            formattedSearchAddress.zip
          addressComponents.street =
            addressComponents.street ||
            formattedAddress.street ||
            formattedSearchAddress.street
          addressComponents.city =
            addressComponents.city ||
            formattedAddress.city ||
            formattedSearchAddress.city
          addressComponents.state =
            addressComponents.state ||
            formattedAddress.state ||
            formattedSearchAddress.state
          return { address: addressComponents, latLng }
        } else {
          return { address: addressComponents, latLng }
        }
      }
    } catch (ex) {
      logException(ex)
      throw new Error(CANT_PARSE_ADDRESS)
    }
  } else {
    throw new Error('Address not found. Please enter a valid address.')
  }
}

export async function getAddressByPlaceId(placeId) {
  const location = await geocodeByPlaceId(placeId)
  const latLng = {
    lat: location[0].geometry.location.lat(),
    lng: location[0].geometry.location.lng()
  }
  const formattedSearchAddress = formatAddress(location[0])
  return { address: formattedSearchAddress, latLng }
}

// ------------------------------------
// Sagas
// ------------------------------------

export function* watchLocationSearch() {
  while (true) {
    const action = yield take(SEARCH)
    try {
      const { address, latLng } = yield call(
        convertAddress,
        action.payload.address
      )
      yield put({ type: FORMATTED_ADDRESS, payload: address })
      try {
        const results = yield call(apiSaga, searchNearbyLocations, [
          address,
          latLng
        ])
        yield put(locationSearchSucceeded(results))
      } catch (ex) {
        if (ex.message === 'Submit Validation Failed') {
          throw new Error(CANT_PARSE_ADDRESS)
        } else {
          throw ex
        }
      }
    } catch (error) {
      yield put(locationSearchFailed(error))
    }
  }
}

export function* locationSearchRedirect() {
  while (true) {
    const {
      payload: { redirect }
    } = yield take(SEARCH)

    yield take(SUCCESS)

    if (redirect) {
      // pull unique dropoffs out of store
      const drops = yield select(getAllDropoffs)
      const dropsCount = drops.length

      // if multiple drops were found, user should pick one
      if (dropsCount > 1) {
        yield put(push(selectLocationRoute.path))

        // if only one drop was found, take the user to the landing page
      } else if (dropsCount === 1) {
        const currentLocationId = yield select(selectUserLocationId)
        const locationError = yield select(selectLocationError)
        if (
          drops[0].id.toString() === currentLocationId.toString() &&
          !locationError
        ) {
          yield put(
            locationSearchFailed(Error('You are already using this location.'))
          )
        } else {
          yield put(
            push(formatUrl(locationRoute.path, { locationId: drops[0].id }))
          )
        }

        // if no drops were found, take the user to the 'join network' page
      } else {
        yield put(push(noLocationRoute.path))
      }
    }
  }
}

export function* watchSubmitBuilding() {
  while (true) {
    const {
      payload: { submitBuildingFormData }
    } = yield take(SUBMIT_BUILDING)
    yield call(
      formSaga,
      'submitBuilding',
      submitBuilding,
      [pascalcaseKeys(submitBuildingFormData)],
      submitBuildingSuccess,
      submitBuildingFailure
    )
  }
}

export function* watchLoadLocationsFromId() {
  while (true) {
    const {
      payload: { id }
    } = yield take(LOAD_LOCATIONS_FROM_ID)
    const { address, latLng } = yield call(getAddressByPlaceId, id)

    yield call(
      apiSaga,
      searchNearbyLocations,
      [address, latLng],
      locationSearchSucceeded,
      locationSearchFailed
    )
  }
}

export function* formatFromAddressId() {
  const {
    payload: { id }
  } = yield take(FORMAT_ADDRESS_FROM_ADDRESSID)
  const { address } = yield call(getAddressByPlaceId, id)
  yield put({ type: FORMATTED_ADDRESS, payload: address })
}
